import { Heart, Loader2 } from 'lucide-react'

import { cn } from '@/lib/utils'

import { Button } from '../../Theme'
import { useWishlistState } from '../state'

const WISHLIST_LABELS = {
  ADD: 'Adauga la favorite',
  REMOVE: 'Sterge de la favorite',
} as const

interface AddToWishlistButtonProps {
  /** Product SKU */
  sku: string
  /** Disabled state */
  disabled?: boolean
  /** Custom className */
  className?: string
  /** Show text label (default: false for icon-only) */
  showLabel?: boolean
}

/**
 * AddToWishlistButton - Button component for adding/removing products from wishlist
 *
 * Uses WishlistProvider context for wishlist operations.
 * Shows loading spinner when processing, filled heart when in wishlist, outline heart when not.
 *
 * @example
 * // Inside a component wrapped with WishlistProvider
 * <AddToWishlistButton sku="SKU123" />
 */
export function AddToWishlistButton({
  sku,
  disabled = false,
  className,
  showLabel = false,
}: AddToWishlistButtonProps) {
  const { handleToggleWishlist, isInWishlist, processingWishlist } = useWishlistState()

  const isLoading = processingWishlist === sku
  const inWishlist = isInWishlist(sku)

  const handleClick = () => {
    if (!isLoading && !disabled) {
      handleToggleWishlist(sku)
    }
  }

  const label = inWishlist ? WISHLIST_LABELS.REMOVE : WISHLIST_LABELS.ADD
  const ariaLabel = inWishlist ? 'Remove from wishlist' : 'Add to wishlist'

  return (
    <Button
      variant="wishlist"
      size="wishlist"
      onClick={handleClick}
      disabled={isLoading || disabled}
      aria-busy={isLoading}
      className={className}
      aria-label={ariaLabel}
      title={label}
    >
      {isLoading ? (
        <Loader2 className="animate-spin" aria-hidden="true" />
      ) : (
        <>
          {showLabel && (
            <span className="font-main-font text-sm md:text-base group-hover/wishlist:text-primary">
              {label}
            </span>
          )}
          <Heart
            aria-hidden="true"
            className={cn(
              'transition-colors duration-300',
              inWishlist
                ? 'text-custom-red fill-current'
                : 'group-hover/wishlist:text-primary',
            )}
          />
        </>
      )}
    </Button>
  )
}
